// groceryProcessor.js - Main entry point for grocery list processing
import { determineMeasurementSystem } from './groceryUtils';
import { getCountryProducts } from './countryProductsLoader';
import { getCountryCurrency } from '../services/countryService';

// Import system-specific processors
import { processGroceryListMetric, consolidateIngredientsMetric, matchIngredientsToProductsMetric } from './metric/groceryProcessorMetric';
import { processGroceryListImperial } from './imperial/groceryProcessorImperial';
import { processUnmatchedIngredientsWithAI } from './aiGroceryFallback';
import { normalizeCategory } from './groceryConstants';

// Import budget to segment conversion function
import { determineBudgetGoal } from './promptGenerators';

/**
 * Process a meal plan into a grocery list with the appropriate measurement system
 * @param {Array} mealPlan - The meal plan with recipes
 * @param {String} country - Country code
 * @param {Number} numberOfPeople - Number of people
 * @param {Object|Number} budget - User's budget (object with min/max or number)
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - Processing options (e.g., skipAIFallback)
 * @returns {Promise<Object>} Processed grocery list
 */
export const processGroceryList = async (mealPlan, country, numberOfPeople, budget = null, onProgress = null, options = {}) => {
  try {
    if (onProgress) {
      onProgress({
        step: 'loading',
        progress: 10,
        message: `Loading local products for ${country}...`,
      });
    }

    // Determine measurement system
    const measurementSystem = determineMeasurementSystem(country);
    
    // Get country's currency
    const currency = getCountryCurrency(country);

    // Convert budget to userSegment if budget is provided
    let userSegment = 'standard'; // Default segment
    if (budget !== null) {
      const budgetGoal = determineBudgetGoal(budget, country, numberOfPeople);
      userSegment = budgetGoal.toLowerCase(); // Convert to lowercase for evaluation functions
    }
    
    // Load country products
    const countryProducts = await getCountryProducts(country);
    
    if (!countryProducts) {
      throw new Error(`No product database for ${country}`);
    }
    
    if (onProgress) {
      onProgress({ 
        step: 'processing',
        progress: 20,
        message: `Putting your grocery list together...`,
      });
    }
    
    // Create context object with common data
    const context = {
      country,
      currency: currency.symbol,
      measurementSystem,
      userSegment // Add userSegment to context
    };
    
    // Call the appropriate processor
    if (measurementSystem === 'imperial') {
      return processGroceryListImperial(mealPlan, countryProducts, context, 1, onProgress, options);
    } else {
      return processGroceryListMetric(mealPlan, countryProducts, context, 1, onProgress, options);
    }
  } catch (error) {
    console.error('Error in processGroceryList:', error);
    if (onProgress) {
      onProgress({ step: 'error', message: error.message });
    }
    return { error: error.message };
  }
};

/**
 * Lightweight ingredient matching for a batch of recipes (e.g. 3 recipes of one meal type).
 * Runs only Phases 1-3: consolidate → Typesense match → AI fallback.
 * Skips price calculation, purchase quantities, leftovers, scaling.
 * Returns products grouped by category in the same format as generateGroceryList().products,
 * suitable for passing to queueAllRecipesForProcessing / buildIngredientContext.
 *
 * @param {Array} recipes - Subset of recipes (e.g. 3 Breakfast recipes)
 * @param {string} country - Country code
 * @param {number} numberOfPeople - Number of people (unused for scaling, kept for API compat)
 * @returns {Promise<Array>} [{category, items: [...]}] — grouped matched products
 */
export const matchIngredientsForBatch = async (recipes, country, numberOfPeople = 1, options = {}) => {
  try {
    // Phase 6: lightweight mode = Pass 1 only, no AI synonyms, no AI selection, no AI
    // unmatched fallback. Used for per-recipe prematch during streaming to avoid 429
    // storms against the backend AI proxy (the same backend is shared with the final
    // grocery generation step that runs Pass 1+2 properly).
    const lightweight = !!options.lightweight;

    // Phase 1: Consolidate ingredients from batch
    const consolidated = consolidateIngredientsMetric(recipes, numberOfPeople);

    if (!consolidated || consolidated.length === 0) {
      console.warn('[matchIngredientsForBatch] No ingredients consolidated');
      return [];
    }

    console.log(`[matchIngredientsForBatch] Consolidated ${consolidated.length} ingredients from ${recipes.length} recipes (lightweight=${lightweight})`);

    // Phase 2: Match to Typesense (Pass 1 only when lightweight)
    const matched = await matchIngredientsToProductsMetric(consolidated, null, country, { skipAI: lightweight });

    // Phase 3: AI fallback for unmatched (skipped in lightweight mode)
    const unmatchedItems = matched.filter(item => item.unmatched);
    let allProducts = [...matched];

    if (!lightweight && unmatchedItems.length > 0) {
      console.log(`[matchIngredientsForBatch] ${unmatchedItems.length} unmatched → AI fallback`);
      const aiResults = await processUnmatchedIngredientsWithAI(unmatchedItems, country);
      allProducts = matched.filter(item => !item.unmatched);
      allProducts.push(...aiResults);
    } else if (lightweight && unmatchedItems.length > 0) {
      console.log(`[matchIngredientsForBatch] [lightweight] ${unmatchedItems.length} unmatched — left as unresolved (no AI fallback)`);
    }

    // Group by category (same format as processGroceryListMetric output)
    const grouped = {};
    allProducts.forEach(item => {
      const cat = normalizeCategory(item.category || 'Other');
      if (!grouped[cat]) grouped[cat] = { category: cat, items: [] };
      grouped[cat].items.push(item);
    });

    const result = Object.values(grouped);

    // Phase 4: surface unresolved ingredients. An ingredient counts as unresolved when:
    //   - it has no Typesense productId (AI fallback only or true no-match), OR
    //   - it was explicitly flagged unmatched (no hit, form mismatch, AI rejected), OR
    //   - lightweight Pass 1 rejected it on form mismatch (_unmatchReason === 'pass1_form_mismatch'), OR
    //   - AI selection returned a low-certainty match (<0.5) in the full path.
    // This drives the Phase 4 retry in useMealPlanLoader.
    const unresolved = allProducts
      .filter(item => (
        !item.productId
        || item.unmatched
        || item._unmatchReason === 'pass1_form_mismatch'
        || (typeof item.matchConfidence === 'number' && item.matchConfidence > 0 && item.matchConfidence < 0.5)
      ))
      .map(item => {
        let reason;
        if (item._unmatchReason === 'pass1_form_mismatch') reason = 'pass1_form_mismatch';
        else if (item._unmatchReason === 'pass1_no_hit_skip_ai') reason = 'pass1_no_hit_skip_ai';
        else if (item._unmatchReason === 'pass1_no_hit') reason = 'pass1_no_hit';
        else if (!item.productId) reason = 'no_typesense_match_ai_fallback_only';
        else if (item.unmatched) reason = 'unmatched';
        else if (typeof item.matchConfidence === 'number' && item.matchConfidence < 0.5) reason = 'low_ai_certainty';
        else reason = 'unknown';
        return {
          name: item.name,
          originalName: item.originalName,
          productHint: item.productHint || null,
          form: item.form || null,
          usedIn: item.usedIn || [],
          reason,
          matchConfidence: typeof item.matchConfidence === 'number' ? item.matchConfidence : null,
          rejectedPass1: item._rejectedPass1 || null,
        };
      });
    Object.defineProperty(result, '_unresolved', {
      value: unresolved,
      enumerable: false,
      writable: false,
      configurable: false,
    });

    console.log(`[matchIngredientsForBatch] Done: ${allProducts.length} products in ${result.length} categories; unresolved=${unresolved.length} (lightweight=${lightweight})`);
    return result;
  } catch (error) {
    console.error('[matchIngredientsForBatch] Error:', error);
    return [];
  }
};